import React from "react";
import { commitLocalUpdate, commitMutation, fetchQuery, graphql, useFragment } from "react-relay";
import type { OnChangeValue } from "react-select";

import { faChevronRight, faEnvelope } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";

import { type Channel, SlackChannelSelect } from "~/common/SlackChannelSelect";
import { type Conversation, TeamsConversationSelect } from "~/common/TeamsConversationSelect";
import { type Team, TeamsTeamSelect } from "~/common/TeamsTeamSelect";
import { type Channel as ZoomChannel, ZoomChannelSelect } from "~/common/ZoomChannelSelect";
import { ReactComponent as ConfluenceIcon } from "~/legacy-assets/images/logos/confluence-icon-solid.svg";
import { ReactComponent as GoogleDriveLogo } from "~/legacy-assets/images/logos/google_drive.svg";
import { ReactComponent as HubspotLogo } from "~/legacy-assets/images/logos/hubspot.svg";
import { ReactComponent as TeamsMark } from "~/legacy-assets/images/logos/microsoft-teams-icon.svg";
import { ReactComponent as TeamsLogo } from "~/legacy-assets/images/logos/microsoft-teams-logo.svg";
import { ReactComponent as NotionLogo } from "~/legacy-assets/images/logos/notion-logo.svg";
import { ReactComponent as SalesforceLogo } from "~/legacy-assets/images/logos/salesforce.svg";
import { ReactComponent as SlackMark } from "~/legacy-assets/images/logos/slack-mark.svg";
import { ReactComponent as SlackLogo } from "~/legacy-assets/images/logos/slack.svg";
import { ReactComponent as ZapierLogo } from "~/legacy-assets/images/logos/zapier.svg";
import { ReactComponent as ZoomLogo } from "~/legacy-assets/images/logos/zoom_icon_only.svg";
import { promptGoogleAuth } from "~/lib/auth";
import {
	promptConfluenceAuth,
	promptHubspotAuth,
	promptOffice365Auth,
	promptSalesforceAuth,
	promptSlackAuth,
} from "~/lib/auth/index";
import { smart } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError } from "~/lib/utils";
import { isTeams, isZoom } from "~/lib/utils";
import { useLongestMeetingRecording } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import ConfluenceSendNotesModal from "~/notes/Confluence/ConfluenceSendNotes";
import NotionSendNotesModal from "~/notes/Notion/NotionSendNotes";
import HubspotSendNotesModal from "~/stream/SideBar/CRMPanel/Hubspot/HubspotSendNotes";
import SalesforceSendNotesModal from "~/stream/SideBar/CRMPanel/Salesforce/SalesforceSendNotes";
import { Button } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import toast from "~/ui/toast";

import PaidFeatureModal from "../billing/PaidFeatureModal";
import DocsExportModal from "../stream/DocsExportModal";
import { TASK } from "../stream/SideBar/CRMPanel/Salesforce/constants";
import type { NotionPage } from "./Notion/NotionPageSelect";
import SendNotesRecording from "./SendNotesRecordingModal";

import type { SendNotesModal_IncludeAISummaryFormField_note$key } from "~/__generated__/SendNotesModal_IncludeAISummaryFormField_note.graphql";
import type { SendNotesModal_note$data as Note } from "~/__generated__/SendNotesModal_note.graphql";
import type { SendNotesModal_Timing_note$key } from "~/__generated__/SendNotesModal_Timing_note.graphql";
import type { SendNotesModal_viewer$data as Viewer } from "~/__generated__/SendNotesModal_viewer.graphql";
import type { SendNotesModalPostToSlackMutation as SlackMutationType } from "~/__generated__/SendNotesModalPostToSlackMutation.graphql";
import type { SendNotesModalPostToTeamsMutation as TeamsMutationType } from "~/__generated__/SendNotesModalPostToTeamsMutation.graphql";
import type { SendNotesModalPostToZoomMutation as ZoomMutationType } from "~/__generated__/SendNotesModalPostToZoomMutation.graphql";
import type { SendNotesModalQuery as QueryType } from "~/__generated__/SendNotesModalQuery.graphql";
import type { SendNotesModalSendNoteToZapierMutation as SendNoteToZapierMutationType } from "~/__generated__/SendNotesModalSendNoteToZapierMutation.graphql";

import styles from "./css/SendNotesModal.module.css";

const SendNotesModalPostToSlackMutation = graphql`
	mutation SendNotesModalPostToSlackMutation($input: PostNoteToSlackInput!) {
		postNoteToSlack(input: $input) {
			ok
		}
	}
`;

const SendNotesModalPostToTeamsMutation = graphql`
	mutation SendNotesModalPostToTeamsMutation($input: PostNoteToTeamsInput!) {
		postNoteToTeams(input: $input) {
			ok
		}
	}
`;

const SendNotesModalPostToZoomMutation = graphql`
	mutation SendNotesModalPostToZoomMutation($input: PostNoteToZoomInput!) {
		postNoteToZoom(input: $input) {
			ok
		}
	}
`;

const SendNotesModalSendNoteToZapierMutation = graphql`
	mutation SendNotesModalSendNoteToZapierMutation($input: SendNoteToZapierInput!) {
		sendNoteToZapier(input: $input) {
			ok
		}
	}
`;

const SendNotesModalQuery = graphql`
	query SendNotesModalQuery($noteId: ID!) {
		viewer {
			integrations {
				slack {
					scopes
				}
				office365 {
					scopes
				}
				zapier {
					isConnected
					hasNotePushHook
				}
				google {
					isActive
				}
				notion {
					isConnected
				}
				confluence {
					isConnected
					requiresReconnect
				}
			}
		}
		note: node(id: $noteId) {
			... on Note {
				sendNoteToCrmAutomationIsValid
				sendNoteToHubspotAutomationIsActive
				sendNoteToSalesforceAutomationIsActive
				meetingRecordings {
					id
				}
			}
		}
	}
`;

const isChannel = (channel: SendNotesModalState["channel"]): channel is Channel => {
	return !!channel;
};

const isZoomChannel = (channel: SendNotesModalState["zoomChannel"]): channel is ZoomChannel => {
	return !!channel;
};

const isTeam = (team: SendNotesModalState["team"]): team is Team => {
	return !!team;
};

const isConversation = (conversation: SendNotesModalState["conversation"]): conversation is Conversation => {
	return !!conversation;
};

const timingFragment = graphql`
	fragment SendNotesModal_Timing_note on Note {
		eventStart
		eventEnd
		eventTitle
		noteTitle: title
	}
`;

export function Timing({ note: noteKey }: { note: SendNotesModal_Timing_note$key }) {
	const note = useFragment(timingFragment, noteKey);

	const noteStartTime = moment(note.eventStart || "");
	const noteEndTime = moment(note.eventEnd || "");

	return (
		<div>
			<div>{note.eventTitle || note.noteTitle}</div>
			{note.eventStart && note.eventEnd && (
				<div className={styles.secondary}>{`${noteStartTime.format("MMMM Do, h:mm")} - ${noteEndTime.format(
					"h:mm a",
				)}`}</div>
			)}
		</div>
	);
}

interface SendNotesModalProps {
	note: Note;
	onClose: () => void;
	viewer: Viewer;
}

interface SendNotesModalState {
	postToSlack: boolean;
	sendByEmail: boolean;
	postToTeams: boolean;
	sendViaZapier: boolean;
	sendViaSalesforce: boolean;
	sendViaHubspot: boolean;
	sendViaGoogleDrive: boolean;
	sendViaNotion: boolean;
	sendViaConfluence: boolean;
	sendViaZoom: boolean;
	message: string;
	channel: OnChangeValue<Channel, false>;
	zoomChannel: OnChangeValue<ZoomChannel, false>;
	team: OnChangeValue<Team, false>;
	conversation: OnChangeValue<Conversation, false>;
	needsSlackPerms: boolean;
	needsTeamsPerms: boolean;
	needsSalesforcePerms: boolean;
	needsHubspotPerms: boolean;
	loading: boolean;
	sendingNote: boolean;
	page: OnChangeValue<NotionPage, false>;
	includeAISummary: boolean;
	sendNoteToCrmAutomationIsValid: boolean;
	sendNoteToHubspotAutomationIsActive: boolean;
	sendNoteToSalesforceAutomationIsActive: boolean;
}

const defaultState: SendNotesModalState = {
	postToSlack: false,
	sendByEmail: false,
	postToTeams: false,
	sendViaZapier: false,
	sendViaSalesforce: false,
	sendViaHubspot: false,
	sendViaGoogleDrive: false,
	sendViaNotion: false,
	sendViaConfluence: false,
	sendViaZoom: false,
	message: "",
	channel: null,
	zoomChannel: null,
	team: null,
	conversation: null,
	needsSlackPerms: true,
	needsTeamsPerms: false,
	needsSalesforcePerms: false,
	needsHubspotPerms: false,
	loading: false,
	sendingNote: false,
	page: null,
	includeAISummary: true,
	sendNoteToCrmAutomationIsValid: false,
	sendNoteToHubspotAutomationIsActive: false,
	sendNoteToSalesforceAutomationIsActive: false,
};

export default smart(
	class SendNotesModal extends React.Component<SendNotesModalProps, SendNotesModalState> {
		static Fragments = {
			viewer: graphql`
				fragment SendNotesModal_viewer on Viewer {
					user {
						isGuest
					}
					googleDocsConnected
					integrations {
						office365 {
							id
						}
						zapier {
							isConnected
							hasNotePushHook
						}
						salesforce {
							id
							isConnected
							lastUsedSalesforceInstanceType
							enhancedNotesEnabled
						}
						hubspot {
							id
							isConnected
						}
						google {
							isActive
						}
						notion {
							id
							isConnected
						}
						confluence {
							id
							isConnected
							requiresReconnect
						}
					}
					account {
						integrations {
							hubspot {
								userConnected
							}
							zoomBot {
								isConnected
							}
						}
						billingAccount {
							hasAccessToHubspotFeature: hasAccessToFeature(feature: hubspot_integration)
							hasAccessToSalesforceFeature: hasAccessToFeature(feature: salesforce_integration)
							hasAccessToZapierIntegration: hasAccessToFeature(feature: zapier_integration)
							hasAccessToNotesIntegrationExport: hasAccessToFeature(feature: notes_integration_export)
						}
					}
				}
			`,

			note: graphql`
				fragment SendNotesModal_note on Note {
					id
					eventStart
					eventEnd
					finished
					noteTitle: title
					noteType
					version
					meetingRecordings {
						id
					}
					stream {
						id
						isExternal
					}
					...SendNotesModal_IncludeAISummaryFormField_note
					...SendNotesModal_Timing_note
				}
			`,
		};

		state: SendNotesModalState = defaultState;

		/* eslint-disable camelcase */
		analyticsProperties = {
			note_id: this.props.note.id,
			note_event_start: this.props.note.eventStart,
			note_event_end: this.props.note.eventEnd,
			note_is_finished: this.props.note.finished,
			note_type: this.props.note.noteType,
			note_version: this.props.note.version,
		};
		/* eslint-enable camelcase */

		onStepToEmail = (): void => {
			this.setState({
				sendByEmail: true,
				includeAISummary: defaultState.includeAISummary,
			});
		};

		onStepToSlack = (): void => {
			const auth = this.state.needsSlackPerms ? promptSlackAuth().then(() => null) : Promise.resolve(null);

			auth.then(() => {
				this.setState({ postToSlack: true, channel: null, includeAISummary: defaultState.includeAISummary });
			});
		};

		onStepToTeams = (): void => {
			const auth = this.state.needsTeamsPerms
				? promptOffice365Auth({ teams: true }).then(() => null)
				: Promise.resolve(null);

			auth.then(() => {
				this.setState({
					postToTeams: true,
					team: null,
					conversation: null,
					includeAISummary: defaultState.includeAISummary,
				});
			});
		};

		onStepToZoom = (): void => {
			this.setState({ sendViaZoom: true, channel: null, includeAISummary: defaultState.includeAISummary });
		};

		onStepToSalesforce = (): void => {
			const auth = this.state.needsSalesforcePerms ? promptSalesforceAuth().then(() => null) : Promise.resolve(null);

			auth.then(() => {
				this.setState({ sendViaSalesforce: true, channel: null, includeAISummary: defaultState.includeAISummary });
			});
		};

		onStepToHubspot = (): void => {
			const auth = this.state.needsHubspotPerms ? promptHubspotAuth().then(() => null) : Promise.resolve(null);

			auth.then(() => {
				this.setState({ sendViaHubspot: true, channel: null, includeAISummary: defaultState.includeAISummary });
			});
		};

		onStepToZapier = (): void =>
			this.setState({ sendViaZapier: true, message: "", includeAISummary: defaultState.includeAISummary });

		onStepToGoogleDrive = (): void => {
			this.setState({ sendViaGoogleDrive: true, message: "", includeAISummary: defaultState.includeAISummary });
		};

		onStepToNotion = (): void =>
			this.setState({ sendViaNotion: true, message: "", includeAISummary: defaultState.includeAISummary });

		onStepToConfluence = (): void =>
			this.setState({ sendViaConfluence: true, message: "", includeAISummary: defaultState.includeAISummary });

		componentDidMount() {
			fetchQuery<QueryType>(environment, SendNotesModalQuery, { noteId: this.props.note.id })
				.toPromise()
				.then(resp => {
					if (!resp) return;
					const { viewer, note } = resp;
					if (viewer.integrations.slack && viewer.integrations.slack[0]) {
						const { scopes } = viewer.integrations.slack[0];
						this.setState({
							needsSlackPerms: !(scopes.includes("chat:write") && scopes.includes("channels:read")),
						});
					}
					if (viewer.integrations.office365) {
						const { scopes } = viewer.integrations.office365;
						this.setState({
							needsTeamsPerms: !(scopes.includes("Team.ReadBasic.All") && scopes.includes("Channel.ReadBasic.All")),
						});
					}
					if (note) {
						this.setState({
							sendNoteToCrmAutomationIsValid: note.sendNoteToCrmAutomationIsValid ?? false,
							sendNoteToHubspotAutomationIsActive: note.sendNoteToHubspotAutomationIsActive ?? false,
						});
					}

					this.setState({ loading: false });
				});
		}

		renderHeader(title: React.ReactNode): React.ReactNode {
			return (
				<Modal.Header onClose={this.onClose}>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>
			);
		}

		renderSelectChannel(): React.ReactNode {
			const { hubspot, salesforce } = this.props.viewer.integrations;
			const { billingAccount, integrations: integrationsAccount } = this.props.viewer.account;

			const hubspotAccount = integrationsAccount.hubspot;
			const hubspotEnabled =
				(hubspot?.isConnected || hubspotAccount?.userConnected) && billingAccount?.hasAccessToHubspotFeature;
			const salesforceEnabled = salesforce?.isConnected && billingAccount?.hasAccessToSalesforceFeature;
			const zoomBotConnected = integrationsAccount.zoomBot?.isConnected;
			const isExternalStream = this.props.note.stream.isExternal;

			return (
				<>
					<Button size="wide" key="via-email" onClick={this.onStepToEmail} className={styles.viaButton}>
						<div className={styles.buttonText}>
							<FontAwesomeIcon className={styles.buttonIcon} icon={faEnvelope} />
							Send via email
						</div>
						<div className={styles.buttonChevron}>
							<FontAwesomeIcon icon={faChevronRight} />
						</div>
					</Button>
					{!isExternalStream && !this.props.viewer.user.isGuest && !Boolean(isTeams) && (
						<Button size="wide" key="via-slack" onClick={this.onStepToSlack} className={styles.viaButton}>
							<div className={styles.buttonText}>
								<SlackMark className={styles.buttonIcon} /> Send via Slack
							</div>
							<div className={styles.buttonChevron}>
								<FontAwesomeIcon icon={faChevronRight} />
							</div>
						</Button>
					)}
					{!isExternalStream && !this.props.viewer.user.isGuest && this.props.viewer.integrations.office365?.id && (
						<Button size="wide" key="via-teams" onClick={this.onStepToTeams} className={styles.viaButton}>
							<div className={styles.buttonText}>
								<TeamsMark className={styles.buttonIcon} /> Send via Teams
							</div>
							<div className={styles.buttonChevron}>
								<FontAwesomeIcon icon={faChevronRight} />
							</div>
						</Button>
					)}
					{!isExternalStream && !this.props.viewer.user.isGuest && !Boolean(isTeams) && zoomBotConnected && (
						<Button size="wide" key="via-zoom" onClick={this.onStepToZoom} className={styles.viaButton}>
							<div className={styles.buttonText}>
								<ZoomLogo className={styles.buttonIcon} /> Send via Zoom
							</div>
							<div className={styles.buttonChevron}>
								<FontAwesomeIcon icon={faChevronRight} />
							</div>
						</Button>
					)}
					{!isExternalStream && !this.props.viewer.user.isGuest && salesforceEnabled && (
						<Button size="wide" key="via-salesforce" onClick={this.onStepToSalesforce} className={styles.viaButton}>
							<div className={styles.buttonText}>
								<SalesforceLogo className={styles.buttonIcon} style={{ color: "#00A1E0" }} /> Send to Salesforce
							</div>
							<div className={styles.buttonChevron}>
								<FontAwesomeIcon icon={faChevronRight} />
							</div>
						</Button>
					)}
					{!isExternalStream && !this.props.viewer.user.isGuest && hubspotEnabled && (
						<Button size="wide" key="via-hubspot" onClick={this.onStepToHubspot} className={styles.viaButton}>
							<div className={styles.buttonText}>
								<HubspotLogo className={styles.buttonIcon} /> Send to HubSpot
							</div>
							<div className={styles.buttonChevron}>
								<FontAwesomeIcon icon={faChevronRight} />
							</div>
						</Button>
					)}
					<Button size="wide" key="via-zapier" onClick={this.onStepToZapier} className={styles.viaButton}>
						<div className={styles.buttonText}>
							<ZapierLogo className={styles.buttonIcon} /> Send via Zapier
						</div>
						<div className={styles.buttonChevron}>
							<FontAwesomeIcon icon={faChevronRight} />
						</div>
					</Button>
					{(this.props.viewer.user.isGuest || !!this.props.viewer.integrations.google?.isActive) &&
						!Boolean(isTeams) &&
						!isZoom && (
							<Button
								size="wide"
								key="via-google-drive"
								onClick={this.onStepToGoogleDrive}
								className={styles.viaButton}
							>
								<div className={styles.buttonText}>
									<GoogleDriveLogo className={styles.buttonIcon} /> Export as Google Doc
								</div>
								<div className={styles.buttonChevron}>
									<FontAwesomeIcon icon={faChevronRight} />
								</div>
							</Button>
						)}
					{!isExternalStream &&
						!this.props.viewer.user.isGuest &&
						this.props.viewer.integrations.notion?.isConnected &&
						!isZoom && (
							<Button size="wide" key="via-notion" onClick={this.onStepToNotion} className={styles.viaButton}>
								<div className={styles.buttonText}>
									<NotionLogo className={styles.buttonIcon} /> Export to Notion
								</div>
								<div className={styles.buttonChevron}>
									<FontAwesomeIcon icon={faChevronRight} />
								</div>
							</Button>
						)}
					{!isExternalStream &&
						!this.props.viewer.user.isGuest &&
						this.props.viewer.integrations.confluence?.isConnected &&
						!isZoom && (
							<Button size="wide" key="via-confluence" onClick={this.onStepToConfluence} className={styles.viaButton}>
								<div className={styles.buttonText}>
									<ConfluenceIcon className={styles.buttonIcon} /> Export to Confluence
								</div>
								<div className={styles.buttonChevron}>
									<FontAwesomeIcon icon={faChevronRight} />
								</div>
							</Button>
						)}
				</>
			);
		}

		onChannelChange = (value: OnChangeValue<Channel, false>): void => {
			this.setState({ channel: value });
		};

		onZoomChannelChange = (value: OnChangeValue<ZoomChannel, false>): void => {
			this.setState({ zoomChannel: value });
		};

		onTeamChange = (value: OnChangeValue<Team, false>): void => {
			this.setState({ team: value });
		};

		onTeamsConversationChange = (value: OnChangeValue<Conversation, false>): void => {
			this.setState({ conversation: value });
		};

		onMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
			this.setState({ message: e.currentTarget.value });
		};

		onIncludeAISummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			this.setState({ includeAISummary: e.target.checked });
		};

		onCancel = (): void => {
			this.setState({
				loading: false,
				postToSlack: false,
				sendByEmail: false,
				postToTeams: false,
				sendViaSalesforce: false,
				sendViaHubspot: false,
				sendViaZapier: false,
				sendViaGoogleDrive: false,
				sendViaNotion: false,
				sendViaConfluence: false,
				sendViaZoom: false,
			});
		};

		postToSlack = (): void => {
			this.setState({ sendingNote: true });

			const { channel, message, includeAISummary } = this.state;

			// Should show red boxes/errors here
			if (!channel) return;
			if (!isChannel(channel)) return;

			const input = channel.isUser ? { slackMember: channel.id } : { slackChannel: channel.id };

			dispatchAnalyticsTrack("Note Shared", {
				method: (channel as Channel).isPrivate ? "slack_private_channel" : "slack_public_channel",
				...this.analyticsProperties,
			});

			commitMutation<SlackMutationType>(environment, {
				mutation: SendNotesModalPostToSlackMutation,
				variables: {
					input: {
						message,
						noteId: this.props.note.id,
						teamId: channel.teamId,
						includeAiSummary: includeAISummary,
						...input,
					},
				},
				onCompleted: ({ postNoteToSlack }, errors) => {
					if (errors && errors.length) {
						const errorMessage = getFirstUserFacingError(errors) ?? "Something went wrong, please contact support.";
						toast.error(errorMessage);
						return;
					}

					if (!postNoteToSlack || !postNoteToSlack.ok)
						return toast.error("You don't have permission to post this meeting's notes.");
					toast.success("Meeting summary posted!");
					return this.onClose();
				},
			});
		};

		renderPostToSlack(): React.ReactNode {
			return (
				<>
					{this.renderHeader(
						<>
							Send meeting notes via <SlackLogo className={cx(styles.integrationLogo, styles.slackLogo)} />
						</>,
					)}
					<Modal.Body className={styles.modalBody}>
						<Timing note={this.props.note} />
						<div className={styles.section}>
							<span className={styles.title}>Send to a conversation:</span>
							<SlackChannelSelect onChange={this.onChannelChange} placeholder="Select a user or channel..." />
							<span className={styles.secondary}>
								Note: to send to private channel &apos;/invite @Fellow&apos; to channel first
							</span>
						</div>
						<div className={styles.section}>
							<label className={styles.title}>Custom Slack message (optional):</label>
							<textarea
								onChange={this.onMessageChange}
								value={this.state.message}
								placeholder="Write a custom message for your Slack post..."
							/>
						</div>
						<IncludeAISummaryFormField
							note={this.props.note}
							value={this.state.includeAISummary}
							onChange={this.onIncludeAISummaryChange}
						/>
					</Modal.Body>
					<Modal.Footer>
						<Button size="normal" variant="secondary" key="cancel" onClick={this.onCancel}>
							Cancel
						</Button>
						<Button
							size="normal"
							variant="primary"
							disabled={!this.state.channel || this.state.sendingNote}
							onClick={this.postToSlack}
						>
							Send
						</Button>
					</Modal.Footer>
				</>
			);
		}

		renderSendByEmail(): React.ReactNode {
			return (
				<>
					{this.renderHeader("Send via email")}
					<React.Suspense
						fallback={
							<Modal.Body>
								<div>Loading...</div>
							</Modal.Body>
						}
					>
						<SendNotesRecording noteId={this.props.note.id} onClose={this.onClose} />
					</React.Suspense>
				</>
			);
		}

		postToTeams = (): void => {
			this.setState({ sendingNote: true });

			const { team, conversation, message, includeAISummary } = this.state;

			// Should show red boxes/errors here
			if (!team) return;
			if (!conversation) return;
			if (!isTeam(team)) return;
			if (!isConversation(conversation)) return;

			const input = { team: team.id, conversation: conversation.id, isUser: conversation.isUser };

			dispatchAnalyticsTrack("Note Shared", {
				method: "teams_channel",
				...this.analyticsProperties,
			});

			commitMutation<TeamsMutationType>(environment, {
				mutation: SendNotesModalPostToTeamsMutation,
				variables: {
					input: {
						message,
						noteId: this.props.note.id,
						includeAiSummary: includeAISummary,
						...input,
					},
				},
				onCompleted: ({ postNoteToTeams }, error) => {
					if (error) return toast.error("Something went wrong, please contact support.");
					if (!postNoteToTeams || !postNoteToTeams.ok)
						return toast.error("You don't have permission to post this meeting's notes.");
					toast.success("Meeting summary posted!");
					return this.onClose();
				},
			});
		};

		postToZoom = (): void => {
			this.setState({ sendingNote: true });

			const { zoomChannel, message, includeAISummary } = this.state;

			if (!zoomChannel) return;
			if (!isZoomChannel(zoomChannel)) return;

			const input = zoomChannel.isUser ? { zoomMember: zoomChannel.id } : { zoomChannel: zoomChannel.id };

			dispatchAnalyticsTrack("Note Shared", {
				method: "zoom_public_channel",
				...this.analyticsProperties,
			});

			commitMutation<ZoomMutationType>(environment, {
				mutation: SendNotesModalPostToZoomMutation,
				variables: {
					input: {
						message,
						noteId: this.props.note.id,
						includeAiSummary: includeAISummary,
						...input,
					},
				},
				onCompleted: ({ postNoteToZoom }, error) => {
					this.setState({ sendingNote: false });
					if (error) return toast.error("Something went wrong, please contact support.");
					if (!postNoteToZoom || !postNoteToZoom.ok)
						return toast.error("You don't have permission to post this meeting's notes.");
					toast.success("Meeting summary posted!");
					return this.onClose();
				},
			});
		};

		renderSendViaZoom(): React.ReactNode {
			return (
				<>
					{this.renderHeader(
						<>
							<ZoomLogo className={styles.integrationLogo} /> Send Note to Zoom
						</>,
					)}
					<Modal.Body className={styles.modalBody}>
						<Timing note={this.props.note} />
						<div className={styles.section}>
							<span className={styles.title}>Send to a channel:</span>
							<ZoomChannelSelect onChange={this.onZoomChannelChange} placeholder="Select a channel..." />
						</div>
						<div className={styles.section}>
							<label className={styles.title}>Custom message (optional):</label>
							<textarea
								onChange={this.onMessageChange}
								value={this.state.message}
								placeholder="Write a custom message for your post in the Zoom chat..."
							/>
						</div>
						<IncludeAISummaryFormField
							note={this.props.note}
							value={this.state.includeAISummary}
							onChange={this.onIncludeAISummaryChange}
						/>
					</Modal.Body>
					<Modal.Footer>
						<Button size="normal" variant="tertiary" key="cancel" onClick={this.onCancel}>
							Cancel
						</Button>
						<Button
							size="normal"
							variant="primary"
							disabled={!this.state.zoomChannel || this.state.sendingNote}
							onClick={this.postToZoom}
						>
							Send
						</Button>
					</Modal.Footer>
				</>
			);
		}

		renderSendViaSalesforce(): React.ReactNode {
			return (
				<>
					{this.renderHeader(<>Send Note to Salesforce</>)}
					<SalesforceSendNotesModal
						noteId={this.props.note.id}
						onClose={this.onCancel}
						options={[]}
						modalMode={false}
						lastTypeUsed={this.props.viewer.integrations.salesforce?.lastUsedSalesforceInstanceType ?? TASK}
						enhancedNotesEnabled={this.props.viewer.integrations.salesforce?.enhancedNotesEnabled ?? false}
					/>
				</>
			);
		}

		renderSendViaHubspot(): React.ReactNode {
			return (
				<>
					{this.renderHeader(<>Send Note to HubSpot</>)}
					<HubspotSendNotesModal
						noteId={this.props.note.id}
						onClose={this.onCancel}
						options={[]}
						modalMode={false}
						sendNotesAutomationIsActive={this.state.sendNoteToHubspotAutomationIsActive}
						shouldShowAutomationInfoBox={this.state.sendNoteToCrmAutomationIsValid}
					/>
				</>
			);
		}

		renderPostToTeams(): React.ReactNode {
			return (
				<>
					{this.renderHeader(
						<>
							Send meeting notes via <TeamsLogo className={styles.integrationLogo} />
						</>,
					)}
					<Modal.Body className={styles.modalBody}>
						<Timing note={this.props.note} />
						<div className={styles.section}>
							<span className={styles.title}>Choose a team:</span>
							<TeamsTeamSelect onChange={this.onTeamChange} placeholder="Select a team..." />
							<span className={styles.secondary}>
								Note: if your team is not on the list please make sure that Fellow is added there first
							</span>
						</div>
						{this.state.team && (
							<div className={styles.section}>
								<span className={styles.title}>Choose a channel/user:</span>
								<TeamsConversationSelect
									onChange={this.onTeamsConversationChange}
									placeholder="Select a channel/user..."
									team={this.state.team}
								/>
								<span className={styles.secondary}>Note: We do not currently support sending to private channels</span>
							</div>
						)}
						<div className={styles.section}>
							<span className={styles.title}>Custom Teams message (optional):</span>
							<textarea
								onChange={this.onMessageChange}
								value={this.state.message}
								placeholder="Write a custom message for your Teams post..."
							/>
						</div>
						<IncludeAISummaryFormField
							note={this.props.note}
							value={this.state.includeAISummary}
							onChange={this.onIncludeAISummaryChange}
						/>
					</Modal.Body>
					<Modal.Footer>
						<Button size="normal" variant="secondary" key="cancel" onClick={this.onCancel}>
							Cancel
						</Button>
						<Button
							size="normal"
							variant="primary"
							disabled={!this.state.team || !this.state.conversation || this.state.sendingNote}
							onClick={this.postToTeams}
						>
							Send
						</Button>
					</Modal.Footer>
				</>
			);
		}

		sendToZapier = () => {
			this.setState({ sendingNote: true });

			dispatchAnalyticsTrack("Note Shared", {
				method: "zapier_channel",
				...this.analyticsProperties,
			});

			commitMutation<SendNoteToZapierMutationType>(environment, {
				mutation: SendNotesModalSendNoteToZapierMutation,
				variables: {
					input: {
						noteId: this.props.note.id,
						customData: this.state.message,
					},
				},
				onCompleted: ({ sendNoteToZapier }, error) => {
					if (error || !sendNoteToZapier?.ok) {
						toast.error("Sorry, something went wrong and we couldn't send the note to Zapier");
					} else if (sendNoteToZapier?.ok) {
						toast.success("The note has been sent to Zapier");
					}
					return this.onClose();
				},
			});
		};

		renderSendViaZapier(): React.ReactNode {
			const { viewer } = this.props;

			const hasAccessToZapierIntegration = viewer.account?.billingAccount?.hasAccessToZapierIntegration;
			const isConnected = viewer.integrations.zapier?.isConnected;
			const hasHook = viewer.integrations.zapier?.hasNotePushHook;

			const promptTitle = (
				<>
					<ZapierLogo className={cx(styles.integrationLogo, styles.zapierLogo)} /> Send note to Zapier
				</>
			);

			if (!hasAccessToZapierIntegration) {
				return <PaidFeatureModal id="zapier_integration" close={this.onCancel} modalContentOnly={true} />;
			} else if (hasHook) {
				return (
					<>
						{this.renderHeader(promptTitle)}
						<Modal.Body className={styles.modalBody}>
							<Timing note={this.props.note} />
							<div className={styles.section}>
								<label className={styles.title}>Custom Zap data (optional):</label>
								<textarea
									onChange={this.onMessageChange}
									value={this.state.message}
									placeholder='Text entered here can be used in Zaps as the "custom_data" value'
								/>
							</div>
						</Modal.Body>
						<Modal.Footer>
							<Button size="normal" variant="secondary" key="cancel" onClick={this.onCancel}>
								Cancel
							</Button>
							<Button size="normal" variant="primary" onClick={this.sendToZapier}>
								Send
							</Button>
						</Modal.Footer>
					</>
				);
			} else if (isConnected) {
				return (
					<>
						{this.renderHeader(promptTitle)}
						<Modal.Body>
							<div>
								It looks like you don&apos;t have any Zapier Zaps set up to work with this trigger yet!{" "}
								<a href="https://zapier.com/app/editor/" target="_blank" rel="noopener">
									Click here to go to Zapier
								</a>
								&nbsp;and create one.
							</div>
						</Modal.Body>
						<Modal.Footer>
							<Button size="normal" variant="secondary" key="cancel" onClick={this.onCancel}>
								Back
							</Button>
						</Modal.Footer>
					</>
				);
			} else {
				return (
					<>
						{this.renderHeader(promptTitle)}
						<Modal.Body>
							<div>
								It looks like you haven&apos;t connected Fellow with Zapier yet!&nbsp;
								<a href="https://zapier.com/apps/fellow/integrations" target="_blank" rel="noopener">
									Sign up or Log in to Zapier
								</a>
								&nbsp;to start integrating thousands of apps with Fellow and creating your own custom automations.
							</div>
						</Modal.Body>
						<Modal.Footer>
							<Button size="normal" variant="secondary" key="cancel" onClick={this.onCancel}>
								Back
							</Button>
						</Modal.Footer>
					</>
				);
			}
		}

		getGoogleDocModal(close: () => void, cancel: () => void): React.ReactNode {
			const hasMeetingRecordings = (this.props.note.meetingRecordings?.length ?? 0) > 0;

			return (
				<DocsExportModal
					noteId={this.props.note.id}
					name={this.props.note.noteTitle}
					streamId={this.props.note.stream.id}
					onClose={close}
					onCancel={cancel}
					hasMeetingRecordings={hasMeetingRecordings}
				/>
			);
		}

		renderPostToGoogleDrive(): React.ReactNode {
			const { viewer } = this.props;

			const hasAccessToNotesIntegrationExport = viewer.account?.billingAccount?.hasAccessToNotesIntegrationExport;
			if (!hasAccessToNotesIntegrationExport) {
				return <PaidFeatureModal id="notes_integration_export" close={this.onCancel} modalContentOnly={true} />;
			}

			const docsConnected = viewer.googleDocsConnected;

			if (!docsConnected) {
				promptGoogleAuth({ docs: true })
					.then(() => {
						this.onClose();
						this.showDocExportModal();
						return <></>;
					})
					.catch(({ code, message: oauthMessage }) => {
						if (code == "access_denied") {
							this.onCancel();
							return toast.error(`Fellow requires access to Google Docs: (${oauthMessage})`);
						} else {
							this.onCancel();
							return toast.error(oauthMessage);
						}
					});
				this.onCancel();
				return <></>;
			} else {
				return this.getGoogleDocModal(this.onClose, this.onCancel);
			}
		}

		showDocExportModal = () => {
			__globalModalContext?.open(({ close }) => (
				<Modal onClose={close} width="23.75rem">
					{this.getGoogleDocModal(close, close)}
				</Modal>
			));
		};

		sendToNotionAnalytics = () => {
			dispatchAnalyticsTrack("Note Shared", {
				method: "notion_page",
				...this.analyticsProperties,
			});
		};

		renderSendViaNotion(): React.ReactNode {
			const { viewer } = this.props;

			const hasAccessToNotesIntegrationExport = viewer.account?.billingAccount?.hasAccessToNotesIntegrationExport;
			if (!hasAccessToNotesIntegrationExport) {
				return <PaidFeatureModal id="notes_integration_export" close={this.onCancel} modalContentOnly={true} />;
			}

			const hasMeetingRecordings = (this.props.note.meetingRecordings?.length ?? 0) > 0;

			return (
				<>
					<Modal.Header onClose={this.onClose}>
						<Modal.Title>
							<NotionLogo className={styles.integrationLogo} /> Export note to Notion
						</Modal.Title>
						<HelpCenterIcon articleId="6442899" title="Exporting notes to Notion" />
					</Modal.Header>
					<NotionSendNotesModal
						noteId={this.props.note.id}
						name={this.props.note.noteTitle}
						onClose={this.onCancel}
						trackAnalytics={this.sendToNotionAnalytics}
						hasMeetingRecordings={hasMeetingRecordings}
					/>
				</>
			);
		}

		sendToConfluenceAnalytics = () => {
			dispatchAnalyticsTrack("Note Shared", {
				method: "confluence_page",
				...this.analyticsProperties,
			});
		};

		renderSendViaConfluence(): React.ReactNode {
			const { viewer } = this.props;

			const hasAccessToNotesIntegrationExport = viewer.account?.billingAccount?.hasAccessToNotesIntegrationExport;
			if (!hasAccessToNotesIntegrationExport) {
				return <PaidFeatureModal id="notes_integration_export" close={this.onCancel} modalContentOnly={true} />;
			}

			const confluenceRequiresReconnect = viewer.integrations.confluence?.requiresReconnect;

			if (confluenceRequiresReconnect) {
				promptConfluenceAuth()
					.then(() => {
						// Update the Relay store
						commitLocalUpdate(environment, store => {
							const root = store.getRoot();
							const viewer = root.getLinkedRecord("viewer");
							const integrations = viewer?.getLinkedRecord("integrations");
							const confluence = integrations?.getLinkedRecord("confluence");

							if (confluence) {
								confluence.setValue(false, "requiresReconnect");
							}
						});

						this.onClose();
						this.showConfluenceSendNotesModal();
					})
					.catch(({ code, message: oauthMessage }) => {
						if (code === "access_denied") {
							this.onCancel();
							return toast.error(`Fellow requires access to Confluence: (${oauthMessage})`);
						} else {
							this.onCancel();
							return toast.error(oauthMessage);
						}
					});
				this.onCancel();
				return <></>;
			} else {
				return this.getConfluenceSendNotesModal(this.onClose, this.onCancel);
			}
		}

		showConfluenceSendNotesModal = () => {
			__globalModalContext?.open(({ close }) => (
				<Modal onClose={close} width="500px">
					{this.getConfluenceSendNotesModal(close, close)}
				</Modal>
			));
		};

		getConfluenceSendNotesModal(close: () => void, cancel: () => void): React.ReactNode {
			const hasMeetingRecordings = (this.props.note.meetingRecordings?.length ?? 0) > 0;

			return (
				<>
					<Modal.Header onClose={close}>
						<Modal.Title>
							<ConfluenceIcon className={styles.integrationLogo} /> Export note to Confluence
						</Modal.Title>
						<HelpCenterIcon articleId="6650085" title="Set up the Confluence integration" />
					</Modal.Header>
					<ConfluenceSendNotesModal
						noteId={this.props.note.id}
						name={this.props.note.noteTitle}
						onClose={cancel}
						trackAnalytics={this.sendToConfluenceAnalytics}
						hasMeetingRecordings={hasMeetingRecordings}
					/>
				</>
			);
		}

		onClose = (): void => {
			this.onCancel();
		};

		render(): React.ReactNode {
			const {
				loading,
				postToSlack,
				sendByEmail,
				postToTeams,
				sendViaSalesforce,
				sendViaHubspot,
				sendViaZapier,
				sendViaGoogleDrive,
				sendViaNotion,
				sendViaConfluence,
				sendViaZoom,
			} = this.state;

			let content: React.ReactNode;
			if (loading) {
				content = <div />;
			} else if (postToSlack) {
				content = this.renderPostToSlack();
			} else if (sendByEmail) {
				content = this.renderSendByEmail();
			} else if (postToTeams) {
				content = this.renderPostToTeams();
			} else if (sendViaZapier) {
				content = this.renderSendViaZapier();
			} else if (sendViaSalesforce) {
				content = this.renderSendViaSalesforce();
			} else if (sendViaHubspot) {
				content = this.renderSendViaHubspot();
			} else if (sendViaGoogleDrive) {
				content = this.renderPostToGoogleDrive();
			} else if (sendViaNotion) {
				content = this.renderSendViaNotion();
			} else if (sendViaConfluence) {
				content = this.renderSendViaConfluence();
			} else if (sendViaZoom) {
				content = this.renderSendViaZoom();
			} else {
				return this.renderSelectChannel();
			}

			return (
				<Modal onClose={this.onClose} width="500px">
					{content}
				</Modal>
			);
		}
	},
);

function IncludeAISummaryFormField({
	note: noteKey,
	value,
	onChange,
}: {
	note: SendNotesModal_IncludeAISummaryFormField_note$key;
	value: boolean;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
	const note = useFragment(
		graphql`
			fragment SendNotesModal_IncludeAISummaryFormField_note on Note {
				meetingRecordings {
					...utils_useLongestMeetingRecording_meetingRecordings
				}
			}
		`,
		noteKey,
	);
	const longestMeetingRecording = useLongestMeetingRecording(note.meetingRecordings);

	if (!longestMeetingRecording) return null;

	return (
		<div className={styles.section}>
			<label className={styles.title}>
				<Checkbox checked={value} onChange={onChange} className={styles.includeAISummaryCheckbox} />
				Include AI summary
			</label>
		</div>
	);
}
